import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "admin" | "partner" | "youth" | "mentor" | "platform_manager";

interface Profile {
  id: string;
  user_id: string;
  phone: string;
  full_name: string;
  email: string | null;
  city: string | null;
  company_name: string | null;
  domain: string | null;
  level: string | null;
  status: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Track if initial auth check is complete - prevents re-showing loader on navigation
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchProfile = async (userId: string) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, user_id, phone, full_name, email, city, company_name, domain, level, status")
        .eq("user_id", userId)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        return;
      }

      setProfile(profileData);

      // Fetch roles
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (rolesError) {
        console.error("Error fetching roles:", rolesError);
        return;
      }

      setRoles(rolesData?.map((r) => r.role as AppRole) || []);
    } catch (error) {
      console.error("Error in fetchProfile:", error);
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await fetchProfile(user.id);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Only synchronous state updates here
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Only show loading on initial auth, not on navigation
        if (!isInitialized) {
          setIsLoading(true);
        }
        // Defer Supabase calls with setTimeout to prevent deadlock
        setTimeout(() => {
          fetchProfile(session.user.id)
            .catch((error) => {
              console.error("Error in deferred fetchProfile:", error);
            })
            .finally(() => {
              setIsLoading(false);
              setIsInitialized(true);
            });
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
        setIsLoading(false);
        setIsInitialized(true);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id)
          .catch((error) => {
            console.error("Error fetching profile on init:", error);
          })
          .finally(() => {
            setIsLoading(false);
            setIsInitialized(true);
          });
      } else {
        setIsLoading(false);
        setIsInitialized(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        isLoading,
        signOut,
        refreshProfile,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
